/**
 * Phones List Tab Component
 *
 * Displays list of phone sources for a project
 */

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Card,
  Empty,
} from 'antd'
import {
  ReloadOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig, SorterResult } from 'antd/es/table/interface'
import api from '@/services/api'

const { Text } = Typography

interface PhonesListTabProps {
  projectId: number
}

interface PhoneSource {
  id: number
  project_id: number
  vdl_id: number | null
  source: string
  source_type: string
  subsource: string | null
  source_from: string | null
  status: string
  will_work: boolean
  locked_by_operator_id: number | null
  locked_at: string | null
  created_at: string
}

const statusColors: Record<string, string> = {
  pending: 'warning',
  pre_approved: 'processing',
  active: 'success',
  inactive: 'default',
  deleted: 'error',
}

const statusLabels: Record<string, string> = {
  pending: 'Ожидание',
  pre_approved: 'Предодобрен',
  active: 'Одобрен',
  inactive: 'Неактивен',
  deleted: 'Удален',
}

const PhonesListTab = forwardRef(({ projectId }: PhonesListTabProps, ref) => {
  const [phones, setPhones] = useState<PhoneSource[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined)

  const fetchPhones = async (
    page: number = 1,
    pageSize: number = 20,
    sortFieldParam?: string,
    sortOrderParam?: 'asc' | 'desc'
  ) => {
    if (!projectId) return

    setLoading(true)

    try {
      const response = await api.getSources({
        project_id: projectId,
        page,
        page_size: pageSize,
        source_type: 'phone',
        sort_by: sortFieldParam !== undefined ? sortFieldParam : sortField,
        sort_order: sortOrderParam !== undefined ? sortOrderParam : sortOrder,
      })

      setPhones(response.items || [])
      setPagination({
        current: response.page || page,
        pageSize: response.page_size || pageSize,
        total: response.total || 0,
      })
    } catch (error) {
      console.error('Fetch phones error:', error)
      message.error('Ошибка загрузки телефонов')
    } finally {
      setLoading(false)
    }
  }

  useImperativeHandle(ref, () => ({
    refresh: () => fetchPhones(pagination.current, pagination.pageSize),
  }))

  useEffect(() => {
    fetchPhones()
  }, [projectId])

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _filters: any,
    sorter: SorterResult<PhoneSource> | SorterResult<PhoneSource>[]
  ) => {
    if (sorter && !Array.isArray(sorter) && sorter.columnKey) {
      const field = sorter.columnKey as string
      const order = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined

      setSortField(order ? field : undefined)
      setSortOrder(order)

      setPagination({
        ...pagination,
        current: 1,
        pageSize: newPagination.pageSize || pagination.pageSize,
      })
      fetchPhones(1, newPagination.pageSize || pagination.pageSize, order ? field : undefined, order)
    } else {
      setPagination({
        ...pagination,
        current: newPagination.current || 1,
        pageSize: newPagination.pageSize || pagination.pageSize,
      })
      fetchPhones(newPagination.current || 1, newPagination.pageSize || pagination.pageSize)
    }
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—'
    try {
      let date: Date
      if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
        date = new Date(dateString)
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        date = new Date(dateString + 'Z')
      } else {
        date = new Date(dateString)
      }

      if (isNaN(date.getTime())) return '—'

      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    } catch (error) {
      return '—'
    }
  }

  const formatPhoneNumber = (phone: string) => {
    // Format Russian phone number
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('7')) {
      return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`
    }
    if (cleaned.length === 10) {
      return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8)}`
    }
    return phone
  }

  const columns: ColumnsType<PhoneSource> = [
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Телефон</span>,
      dataIndex: 'source',
      key: 'source',
      sorter: true,
      render: (phone: string) => (
        <Space>
          <PhoneOutlined style={{ color: '#52c41a' }} />
          <Text strong style={{ fontSize: '13px', fontFamily: 'monospace' }}>
            {formatPhoneNumber(phone)}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Тип</span>,
      dataIndex: 'subsource',
      key: 'subsource',
      width: 120,
      render: (subsource: string | null) => (
        subsource ? (
          <Tag color="green" style={{ margin: 0, fontSize: '11px' }}>
            {subsource}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
        )
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Источник</span>,
      dataIndex: 'source_from',
      key: 'source_from',
      width: 100,
      sorter: true,
      render: (source_from: string | null) => (
        <Tag
          color={source_from === 'parsed' ? 'purple' : source_from === 'manual' ? 'blue' : 'default'}
          style={{ margin: 0, fontSize: '10px' }}
        >
          {source_from || 'Неизвестен'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Статус</span>,
      dataIndex: 'status',
      key: 'status',
      width: 140,
      sorter: true,
      render: (status: string) => (
        <Tag
          color={statusColors[status] || 'default'}
          icon={status === 'active' ? <CheckCircleOutlined /> : status === 'pending' ? <ClockCircleOutlined /> : undefined}
          style={{ margin: 0, fontSize: '11px' }}
        >
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>VDL ID</span>,
      dataIndex: 'vdl_id',
      key: 'vdl_id',
      width: 100,
      align: 'center' as const,
      render: (vdl_id: number | null) => (
        vdl_id ? (
          <Tag color="blue" style={{ margin: 0, fontSize: '11px' }}>{vdl_id}</Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
        )
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Создан</span>,
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: true,
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDate(date)}
        </Text>
      ),
    },
  ]

  return (
    <div>
      {/* Header */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 6,
          border: '1px solid #e8e8e8',
        }}
        bodyStyle={{ padding: '12px 20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <PhoneOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
            <Text strong style={{ fontSize: '14px' }}>Телефоны</Text>
            <Tag color="green">{pagination.total}</Tag>
          </Space>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => fetchPhones(pagination.current, pagination.pageSize)}
            loading={loading}
          >
            Обновить
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card
        style={{
          borderRadius: 6,
          border: '1px solid #e8e8e8',
        }}
        bodyStyle={{ padding: 0 }}
      >
        {phones.length === 0 && !loading ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Нет телефонов для отображения"
            style={{ padding: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={phones}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total: number) => `Всего: ${total}`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            rowKey="id"
            size="small"
          />
        )}
      </Card>
    </div>
  )
})

PhonesListTab.displayName = 'PhonesListTab'

export default PhonesListTab
